// src/utils/rtkQueryErrorHandler.ts

import {
  QueryActionCreatorResult,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import {
  Action,
  ThunkDispatch,
  AnyAction, // <--- Keep this import for the cast
  ThunkAction,
  UnknownAction
} from '@reduxjs/toolkit';

import { utilityApi } from '../features/utilitySlice'; // Adjust path if necessary
import { printError, toStringRepresentation } from './utils'; // Adjust path if needed
import { getVersion } from './hooks';   // Adjust path if needed

// --- ExtendedQueryLifecycleApi (Ensure this interface is present as discussed) ---
export interface ExtendedQueryLifecycleApi<QueryResult, QueryArg, BaseQueryError, AppGlobalState> {
  dispatch: ThunkDispatch<AppGlobalState, any, Action>;
  getState: () => AppGlobalState;
  extra: unknown;
  requestId: string;
  signal: AbortSignal;
  abort: () => void;
  queryFulfilled: Promise<{ data: QueryResult; meta?: unknown }>;
}
// --- End of ExtendedQueryLifecycleApi ---

// --- NEW: Define retry state for each endpoint ---
interface EndpointRetryState {
  timerId: NodeJS.Timeout | null;
  retryCount: number;
}

// --- NEW: Map to hold retry state for each endpoint by name ---
const activeQueryRetryStates = new Map<string, EndpointRetryState>();

// --- Modified clear function: only cancels timer, does NOT reset retryCount ---
const clearScheduledRetry = (endpointName: string): void => {
  const state = activeQueryRetryStates.get(endpointName);
  if (state && state.timerId) {
    clearTimeout(state.timerId);
    state.timerId = null;
  }
};

export const createHandleQueryErrorAndRetry = <AppGlobalState>(
  endpointName: string,
  // Keep this type for initiateFn, as it correctly describes the function being passed
  initiateFn: (
    arg: any,
    options?: { force?: boolean; subscriptionOptions?: { pollingInterval?: number } }
  ) => ThunkAction<
    QueryActionCreatorResult<any>,
    AppGlobalState,
    any,
    AnyAction
  >,
  shouldRetry: (rawError: unknown) => boolean = (rawError: unknown) => true
) => {
  const logToBackEndInitiate = utilityApi.endpoints.logToBackEnd.initiate;

  return async (
    queryArg: unknown,
    api: unknown
  ): Promise<void> => {
    type ExpectedQueryResult = any;
    type ExpectedQueryArg = any;
    type ExpectedBaseQueryError = FetchBaseQueryError;

    // --- NEW: Get or initialize the retry state for this endpoint ---
    let currentRetryState = activeQueryRetryStates.get(endpointName);
    if (!currentRetryState) {
      currentRetryState = { timerId: null, retryCount: 0 };
      activeQueryRetryStates.set(endpointName, currentRetryState);
    }

    // Always clear any *existing scheduled retry* when a new query (manual or otherwise) starts for this endpoint.
    // This effectively ensures only one retry is ever "active" (scheduled) at a time.
    clearScheduledRetry(endpointName);

    const typedApi = api as unknown as ExtendedQueryLifecycleApi<
      ExpectedQueryResult,
      ExpectedQueryArg,
      ExpectedBaseQueryError,
      AppGlobalState
    >;

    const typedQueryArg = queryArg as ExpectedQueryArg;


    try {
      await typedApi.queryFulfilled;
      //clearQueryRetryTimer(endpointName);
      clearScheduledRetry(endpointName); // Clear any pending timer
      currentRetryState.retryCount = 0; // Reset count for next error sequence
    } catch (rawError: unknown) {
      try {
        typedApi.dispatch(logToBackEndInitiate({
          context: `${endpointName}`,
          message: toStringRepresentation(rawError, { status: 'ERROR' }),
          version: getVersion()
        }));
      } catch (logError) {
        console.error(`[${endpointName}] Failed to dispatch log to backend:`, logError);
      }

      // Increment retry count ONLY on failure
      currentRetryState.retryCount++;

      const INITIAL_REFETCH_DELAY_MS: number = 5000;
      // Calculate exponential backoff delay
      const currentDelay = INITIAL_REFETCH_DELAY_MS + currentRetryState.retryCount*2 //INITIAL_REFETCH_DELAY_MS * (2 ** (currentRetryState.retryCount - 1));
      // --- IMPORTANT: Clear any *previous* timer before setting a new one ---
      // This is crucial for the "single scheduled retry" mechanism.
      // If a previous retry was scheduled and this new error happens, cancel the old one.
      clearScheduledRetry(endpointName);
      if( !shouldRetry( rawError)){
        return
      }

      const timerId: NodeJS.Timeout = setTimeout(() => {
        typedApi.dispatch(initiateFn(typedQueryArg, { force: true }) as unknown as UnknownAction);
      }, currentDelay);

      currentRetryState.timerId = timerId; // Store the new timer ID in the state map
      // activeQueryRetryTimers.set(endpointName, timerId);
      // console.log(`[${endpointName}] Scheduled a refetch timer (ID: ${timerId}).`);
    }
  };
};